import { useTranslation } from 'react-i18next'

import { mergeClassName } from '../../../utils/mergeClassName'
import {
  useGetQuotePrice,
  useGetQuoteJobPrice,
  type Quote,
} from '../../../modules/quotes'
import { useGetPromoCodeLabel } from '../../../modules/promoCodes'
import Charges from '../../Charges'
import Price from '../../Price'
import { useQuoteContext } from '../QuoteComponent.context'

type ChargesDetailsProps = {
  quote: Quote
  jobIndex: number
}

const ChargesDetails: React.FC<ChargesDetailsProps> = ({ quote, jobIndex }) => {
  const { t } = useTranslation()
  const {
    promoCode,
    multiJobs,
    selectedJob,
    nbJobs,
    jobId,
    emphasisEstimate = false,
    showPlusTaxes = false,
  } = useQuoteContext()
  const getQuotePrice = useGetQuotePrice()
  const getQuoteJobPrice = useGetQuoteJobPrice()
  const getPromoCodeLabel = useGetPromoCodeLabel()

  const specificJob = !!jobId

  const subtotalLabel = emphasisEstimate ? 'Estimate' : 'Subtotal'

  const job = quote.jobs[jobIndex]
  if (!job) {
    return null
  }

  const {
    price: quotePrice,
  } = getQuotePrice(quote, { promoCode })

  const {
    price: jobPrice,
    originalPrice: jobOriginalPrice,
    discount: jobDiscount,
  } = getQuoteJobPrice(quote, jobIndex, { promoCode })

  return (
    <table className="w-full">
      <tbody>

        <Charges
          charges={job.charges}
          showDetails
        />

        <tr>
          <td colSpan={3}>
            <div className="flex justify-end">
              { jobDiscount && promoCode && (
                <div className="mr-[-5px] flex items-center gap-4">
                  <span className="hidden text-green-700 sm:inline">
                    { promoCode.code }
                  </span>
                  <span className="scale-90">
                    { getPromoCodeLabel(promoCode) }
                  </span>
                </div>
              ) }
            </div>
          </td>
          <td>
            <div className="pt-4 text-right">
              { jobDiscount && (
                <>
                  <div className="text-sm text-neutral-400">
                    <Price amount={jobOriginalPrice} compact />
                  </div>
                  <div className="mb-4 text-sm font-bold text-green-700">
                    <Price amount={jobDiscount} compact />
                  </div>
                </>
              ) }
            </div>
          </td>
        </tr>

        { multiJobs && (
          <tr>
            <td>
              <div className="pb-2 pt-4 text-lg leading-5 md:text-xl">
                { t('quotes.quote.jobSubtotal', { day: selectedJob + 1 }) }
              </div>
            </td>
            <td colSpan={3}>
              <div className="pb-2 pt-4 text-right text-lg md:text-xl">
                <Price
                  amount={jobPrice}
                  compact
                />
              </div>
            </td>
          </tr>
        ) }

        <tr>
          <td className={mergeClassName(
            multiJobs && 'border-t-2 pt-4',
          )}
          >
            <div className="text-lg font-bold leading-6 md:text-xl">
              { multiJobs
                ? t(`quotes.quote.quote${subtotalLabel}MultiJobs`, { nbJobs })
                : t(`quotes.quote.quote${subtotalLabel}`) }
            </div>
          </td>
          <td
            className={mergeClassName(
              multiJobs && 'border-t-2 pt-4',
            )}
            colSpan={3}
          >
            <div className="text-right text-lg font-bold md:text-xl">
              <Price
                amount={specificJob ? jobPrice : quotePrice}
                compact
                showCurrency
              />
              { showPlusTaxes && (
                <div className="text-sm font-normal uppercase text-neutral-600">
                  ({ t('quotes.quote.plusTaxes') })
                </div>
              ) }
            </div>
          </td>
        </tr>

      </tbody>
    </table>
  )
}

export default ChargesDetails
